import { lazy } from 'react';
import { Navigate, useLocation, Route } from 'react-router-dom';
import PublicLayout from 'src/services/public/layout';
import AuthLayout from 'src/services/auth/layout/AuthLayout';
import LazyRouteWrapper from "../LazyRouteWrapper";

const ContactUsMessageSuccessfullySent = lazy(() => import('src/modules/Landing/ContactUsMessageSuccessfullySent'));
const PotentialFreelanceDriverForm = lazy(() => import('src/modules/Landing/PotentialFreelanceDriverForm'));

const IntegrateUnAuthMerchant = lazy(() => import('src/modules/Merchant/IntegrateUnAuthMerchant'));
const BlockedAccount = lazy(() => import('src/views/BlockedAccount'));
const IntegrateByCode = lazy(() => import('src/modules/Merchant/IntegrateByCode'));
const PrintOrderAWB = lazy(() => import('src/modules/Order/PrintOrderAWB'));
const ShowOrderBase64 = lazy(() => import('src/modules/Order/PrintOrderAWB/ShowOrderBase64'));
const OrderAWBWithData = lazy(() => import('src/modules/Order/OrderAWB/OrderAWBWithData'));
const OrderAWBTemplate = lazy(() => import('src/modules/Order/OrderAWB/OrderAWBTemplate'));

const ZIDIntegrationCheckStatus = lazy(() => import('src/views/Integrations/ZIDIntegrationCheckStatus'));
const ZIDIntegrationRedirect = lazy(() => import('src/views/Integrations/ZIDIntegrationRedirect'));

const PrivacyPolicy = lazy(() => import('src/views/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('src/views/TermsAndConditions'));

const Landing = lazy(() => import('src/services/public/views/landing'));
const Features = lazy(() => import('src/services/public/views/features'));
const ContactUs = lazy(() => import('src/services/public/views/contact-us'));

const MapitDmsPricing = lazy(() => import('src/services/public/views/pricing/MapitDms'));
const MapitShipPricing = lazy(() => import('src/services/public/views/pricing/MapitShip'));

const About = lazy(() => import('src/services/public/views/about'));
const LastMileSolution = lazy(() => import('src/services/public/views/solutions').then(module => ({ default: module.LastMileSolution })));
const MapitShipSolution = lazy(() => import('src/services/public/views/solutions').then(module => ({ default: module.MapitShipSolution })));
const LogisticsSolution = lazy(() => import('src/services/public/views/solutions').then(module => ({ default: module.LogisticsSolution })));
const FreeDemo = lazy(() => import('src/services/public/views/free-demo'));
const BlogGallery = lazy(() => import('src/services/public/views/blog/blog-gallery'));
const BlogPost = lazy(() => import('src/services/public/views/blog/blog-post'));
const GetAllOrdersDocumentation = lazy(() => import('src/services/public/views/documentation/GetAllOrdersDocumentation'));
const Documentation = lazy(() => import('src/services/public/views/documentation/Documentation'));
const SubmtiFormStatus = lazy(() => import('src/services/public/views/submit-form-status'));

const ShopifyStatus = lazy(() => import('src/views/Integrations/ShopifyStatus'));
const ShopifyRedirect = lazy(() => import('src/views/Integrations/ShopifyRedirect'));
const ShopifyIntegrationStatus = lazy(() => import('src/views/Integrations/ShopifyIntegrationStatus'));

const WooCommerceIntegrationStatus = lazy(() => import('src/views/Integrations/WooCommerceIntegrationStatus'));

const CheckBeforeEnter = (currentUser) => {
    const location = useLocation()

    if (!currentUser?.merchant && location.pathname === "/merchant/integrations/zid") {
        return <Navigate to="/auth/general?redirect_url=/merchant/integrations/zid" />
    }

    if (location.pathname === "/free-demo") {
        return <AuthLayout />
    }

    if (
        ["/documentation", "/terms-and-conditions", "/privacy", "/success/free-demo", "/success/contact-us"].includes(location.pathname)
    ) {
        return <PublicLayout mode='dark' />
    } else {
        return <PublicLayout mode='light' />
    }

}

const rootRoutes = (currentUser) => (
    <>
        <Route path="" element={CheckBeforeEnter(currentUser)}>
            <Route path="" element={<LazyRouteWrapper><Landing /></LazyRouteWrapper>} />
            <Route path="features" element={<LazyRouteWrapper><Features /></LazyRouteWrapper>} />
            <Route path="contact-us" element={<LazyRouteWrapper><ContactUs /></LazyRouteWrapper>} />

            <Route path="pricing">
                <Route path="mapit-dms" element={<LazyRouteWrapper><MapitDmsPricing /></LazyRouteWrapper>} />
                <Route path="mapit-ship" element={<LazyRouteWrapper><MapitShipPricing /></LazyRouteWrapper>} />
            </Route>

            <Route path="about" element={<LazyRouteWrapper><About /></LazyRouteWrapper>} />
            <Route path="success">
                <Route path="free-demo" element={<LazyRouteWrapper><SubmtiFormStatus /></LazyRouteWrapper>} />
                <Route path="contact-us" element={<LazyRouteWrapper><SubmtiFormStatus /></LazyRouteWrapper>} />
            </Route>
            <Route path="solutions">
                <Route path="last-mile" element={<LazyRouteWrapper><LastMileSolution /></LazyRouteWrapper>} />
                <Route path="mapit-ship" element={<LazyRouteWrapper><MapitShipSolution /></LazyRouteWrapper>} />
                <Route path="logistics" element={<LazyRouteWrapper><LogisticsSolution /></LazyRouteWrapper>} />
                <Route path="" element={<Navigate to="/" />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Route>
            <Route path="privacy" element={<LazyRouteWrapper><PrivacyPolicy /></LazyRouteWrapper>} />
            <Route path="terms-and-conditions" element={<LazyRouteWrapper><TermsAndConditions /></LazyRouteWrapper>} />
            <Route path="documentation" element={<LazyRouteWrapper><Documentation /></LazyRouteWrapper>} />
            <Route path="documentation/get-all-orders" element={<LazyRouteWrapper><GetAllOrdersDocumentation /></LazyRouteWrapper>} />
            <Route path="free-demo" element={<LazyRouteWrapper><FreeDemo /></LazyRouteWrapper>} />
            <Route path="blog" element={<LazyRouteWrapper><BlogGallery /></LazyRouteWrapper>} />
            <Route path="blog/:slug" element={<LazyRouteWrapper><BlogPost /></LazyRouteWrapper>} />
            <Route path="contact-us-message-successfully-sent" element={<LazyRouteWrapper><ContactUsMessageSuccessfullySent /></LazyRouteWrapper>} />
            <Route path="*" element={<Navigate to="errors/404" />} />
        </Route>
        <Route path="awb_data/:id" element={<LazyRouteWrapper><OrderAWBWithData /></LazyRouteWrapper>} />
        <Route path="awb/template" element={<LazyRouteWrapper><OrderAWBTemplate /></LazyRouteWrapper>} />
        <Route path="potential-freelance-driver-register" element={<LazyRouteWrapper><PotentialFreelanceDriverForm /></LazyRouteWrapper>} />
        <Route path="integrate_unauth_merchant" element={<LazyRouteWrapper><IntegrateUnAuthMerchant navbarVisible /></LazyRouteWrapper>} />
        <Route path="integrate/salla" element={<LazyRouteWrapper><IntegrateByCode /></LazyRouteWrapper>} />
        <Route path="order/download/pdf/:id" element={<LazyRouteWrapper><PrintOrderAWB /></LazyRouteWrapper>} />
        <Route path="order/download/base64/:id" element={<LazyRouteWrapper><ShowOrderBase64 /></LazyRouteWrapper>} />
        <Route path="blocked" element={<LazyRouteWrapper><BlockedAccount /></LazyRouteWrapper>} />
        <Route path="integrations/zid" element={<LazyRouteWrapper><ZIDIntegrationRedirect /></LazyRouteWrapper>} />
        <Route path="integrations/zid-status" element={<LazyRouteWrapper><ZIDIntegrationCheckStatus /></LazyRouteWrapper>} />
        <Route path="integrations/shopify" element={<LazyRouteWrapper><ShopifyRedirect /></LazyRouteWrapper>} />
        <Route path="integrations/shopify-status" element={<LazyRouteWrapper><ShopifyStatus /></LazyRouteWrapper>} />
        <Route path="integrations/shopify/status" element={<LazyRouteWrapper><ShopifyIntegrationStatus /></LazyRouteWrapper>} />
        <Route path="integrations/woocommerce-status" element={<LazyRouteWrapper><WooCommerceIntegrationStatus /></LazyRouteWrapper>} />
        <Route path="*" element={<Navigate to="errors/404" />} />
    </>
)

export default rootRoutes;
// const rootRoutes = (currentUser) => ([
// {
//     path: "",
//     element: CheckBeforeEnter(currentUser),
//     children: [
//         { path: "", element: <Landing /> },
//         { path: "features", element: <Features /> },
//         { path: "contact-us", element: <ContactUs /> },
//         { path: "pricing", element: <Pricing /> },
//         { path: "about", element: <About /> },
//         {
//             path: "success", children: [
//                 { path: "free-demo", element: <SubmtiFormStatus /> },
//                 { path: "contact-us", element: <SubmtiFormStatus /> }
//             ]
//         },
//         {
//             path: "solutions",
//             children: [
//                 { path: "last-mile", element: <LastMileSolution /> },
//                 { path: "mapit-ship", element: <MapitShipSolution /> },
//                 { path: "logistics", element: <LogisticsSolution /> },
//                 { path: "", element: <Navigate to="/" /> },
//                 { path: "*", element: <Navigate to="/" /> },
//             ]
//         },
//         {
//             path: "privacy",
//             element: <PrivacyPolicy />
//         },
//         {
//             path: "terms-and-conditions",
//             element: <TermsAndConditions />
//         },
//         { path: "documentation", element: <Documentation /> },
//         { path: "documentation/get-all-orders", element: <GetAllOrdersDocumentation /> },
//         { path: "free-demo", element: <FreeDemo /> },
//         { path: "blog", element: <BlogGallery /> },
//         { path: "blog/:slug", element: <BlogPost /> },
//         { path: "contact-us-message-successfully-sent", element: <ContactUsMessageSuccessfullySent /> },
//         { path: "*", element: <Navigate to="/" /> },
//     ],
// },
// { path: "awb_data/:id", element: <OrderAWBWithData /> },
// { path: "awb/template", element: <OrderAWBTemplate /> },
// {
//     path: "potential-freelance-driver-register",
//     element: <PotentialFreelanceDriverForm />
// },
// {
//     path: "integrate_unauth_merchant",
//     element: <IntegrateUnAuthMerchant navbarVisible />
// },
// { path: "integrate/salla", element: <IntegrateByCode /> },
// { path: "order/download/pdf/:id", element: <PrintOrderAWB /> },
// { path: "order/download/base64/:id", element: <ShowOrderBase64 /> },
// {
//     path: "blocked",
//     element: <BlockedAccount />
// },
// { path: "integrations/zid", element: <ZIDIntegrationRedirect /> },
// { path: "integrations/zid-status", element: <ZIDIntegrationCheckStatus /> },
// {
//     path: "*",
//     element: <Navigate to="errors/404" />
// },
// ]);

// export default rootRoutes;
